import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useAuthorizedSignMutation,
  useAuthorizedSignDraftMutation,
  useCreateAuthorizedSignMutation,
  useUpdateAuthorizedSignMutation,
  useUpdateAuthorizedSignDraftMutation,
  useCreateKeyPersonnelMutation,
  useCreateKeyPersonnelDraftMutation,
  useUpdateKeyPersonnelMutation,
  useUpdateKeyPersonnelDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { getCountryCode } from "../../../../utils/countriesState";
import useCompanyUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { personPayload, personSavePath, validatePerson } from "./peopleFields";

/* The people dialog's behaviour — one implementation for both. */
export const usePersonForm = (spec, { editing, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [countries] = useState(() => getCountryCode());
  const [values, setValues] = useState({
    name: "",
    designation: "",
    email: "",
    phone: "",
    nationality: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  /* All created unconditionally; picked by spec below. */
  const [createSignatory] = useAuthorizedSignMutation();
  const [createSignatoryDraft] = useAuthorizedSignDraftMutation();
  const [linkSignatory] = useCreateAuthorizedSignMutation();
  const [updateSignatory] = useUpdateAuthorizedSignMutation();
  const [updateSignatoryDraft] = useUpdateAuthorizedSignDraftMutation();
  const [createPersonnel] = useCreateKeyPersonnelMutation();
  const [createPersonnelDraft] = useCreateKeyPersonnelDraftMutation();
  const [updatePersonnel] = useUpdateKeyPersonnelMutation();
  const [updatePersonnelDraft] = useUpdateKeyPersonnelDraftMutation();

  const handleUpdateCompanyFollow = useCompanyUpdate();

  const isSignatory = spec.key === "signatory";
  const api = isSignatory
    ? {
        create: createSignatory,
        createDraft: createSignatoryDraft,
        update: updateSignatory,
        updateDraft: updateSignatoryDraft,
      }
    : {
        create: createPersonnel,
        createDraft: createPersonnelDraft,
        update: updatePersonnel,
        updateDraft: updatePersonnelDraft,
      };

  /* Transcribed: an existing record seeds every field. */
  useEffect(() => {
    if (!editing) return;
    setValues({
      name: editing.name || "",
      designation: editing.designation || "",
      email: editing.work_email || "",
      phone: editing.work_mobile || "",
      nationality: editing.nationality || "",
    });
  }, [editing]);

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const submit = async () => {
    const found = validatePerson(values, spec);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const path = personSavePath(
      { verifiedCompany: currentEditingCompany?.verifiedCompany, editing },
      spec
    );
    const payload = personPayload(values, spec);

    setBusy(true);
    try {
      if (path === "updateDraft") {
        await api.updateDraft({
          /* The two endpoints name their draft argument differently. */
          ...(isSignatory
            ? { authorized_signID: editing[spec.draftIdKey] }
            : { key_personnelId: editing[spec.draftIdKey] }),
          data: {
            /* Transcribed: carried only when the draft amends a live row. */
            ...(editing?.[spec.draftLinkKey] != null
              ? { [spec.idKey]: editing[spec.draftLinkKey] }
              : {}),
            current_companyID: companyID,
            ...payload,
            request_type: spec.requestType,
          },
          token,
        }).unwrap();
      } else if (path === "createDraft") {
        await api.createDraft({
          data: {
            ...(editing?.[spec.idKey] ? { [spec.idKey]: editing[spec.idKey] } : {}),
            current_companyID: companyID,
            ...payload,
            request_type: spec.requestType,
          },
          token,
        }).unwrap();
      } else if (path === "update") {
        const res = await api.update({
          data: payload,
          ...(isSignatory
            ? { authorized_signID: editing[spec.idKey] }
            : { key_personnelId: editing[spec.idKey] }),
          token,
        }).unwrap();

        if (res) {
          const result = await handleUpdateCompanyFollow(
            companyID,
            spec.followUpdated,
            token
          );
          if (!result.success) console.error(result.error);
        }
      } else {
        const res = await api.create({
          data: spec.needsLink
            ? payload
            : /* Transcribed: the key-personnel create carries the company, so
                 no separate link call follows. */
              { current_companyID: companyID, ...payload },
          token,
        }).unwrap();

        if (res && spec.needsLink) {
          /* Transcribed: a new signatory is linked to the company second. */
          await linkSignatory({
            data: { [spec.joinKey]: res?.[spec.idKey], companyID },
            token,
          }).unwrap();
        }

        if (res) {
          const result = await handleUpdateCompanyFollow(
            companyID,
            spec.followCreated,
            token
          );
          if (!result.success) console.error(result.error);
        }
      }

      onSaved?.();
    } catch (error) {
      /* The live forms swallow all of these in empty catches, so a failed save
         closed the dialog as though it had worked. See FINDINGS F-040. */
      console.error(`${spec.title} save failed:`, error);
      setErrors({
        form:
          error?.data?.detail ||
          error?.data?.message ||
          `Couldn't save this ${spec.title.toLowerCase()}. Please try again.`,
      });
    } finally {
      setBusy(false);
    }
  };

  return { values, setField, errors, countries, busy, submit };
};

export default usePersonForm;
